import { useEffect } from "react";
import { useI18n } from "../i18n";
import { getPageSeo, SITE_NAME, SITE_URL } from "../seo";

function upsertMeta(
  id: string,
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  let element = document.getElementById(id) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.id = id;
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, key);
  element.content = content;
}

function upsertLink(id: string, attributes: Record<string, string>) {
  let element = document.getElementById(id) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    element.id = id;
    document.head.appendChild(element);
  }
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
}

export function PageMeta({ title }: { title: string }) {
  const { locale } = useI18n();

  useEffect(() => {
    const seo = getPageSeo(window.location.pathname, locale, title);
    document.title = seo.title;
    document.documentElement.lang = locale === "en" ? "en" : "zh-CN";
    document.documentElement.dataset.locale = locale;

    upsertMeta("meta-description", "name", "description", seo.description);
    upsertMeta("meta-robots", "name", "robots", seo.robots);
    upsertMeta("meta-author", "name", "author", SITE_NAME);

    upsertLink("link-canonical", { rel: "canonical", href: seo.canonical });
    upsertLink("link-alternate-zh", {
      rel: "alternate",
      hreflang: "zh-CN",
      href: seo.alternates.zh,
    });
    upsertLink("link-alternate-en", {
      rel: "alternate",
      hreflang: "en",
      href: seo.alternates.en,
    });
    upsertLink("link-alternate-default", {
      rel: "alternate",
      hreflang: "x-default",
      href: seo.alternates.default,
    });
    upsertLink("link-llms", {
      rel: "alternate",
      type: "text/markdown",
      href: `${SITE_URL}/llms.txt`,
      title: "LLM content index",
    });

    const openGraph: Record<string, string> = {
      "og:type": seo.type,
      "og:site_name": SITE_NAME,
      "og:title": seo.title,
      "og:description": seo.description,
      "og:url": seo.canonical,
      "og:image": seo.image,
      "og:image:width": String(seo.imageWidth),
      "og:image:height": String(seo.imageHeight),
      "og:image:alt": seo.title,
      "og:locale": locale === "en" ? "en_US" : "zh_CN",
      "og:locale:alternate": locale === "en" ? "zh_CN" : "en_US",
    };
    for (const [property, content] of Object.entries(openGraph)) {
      upsertMeta(
        `meta-${property.replaceAll(":", "-")}`,
        "property",
        property,
        content,
      );
    }

    const twitter: Record<string, string> = {
      "twitter:card": "summary_large_image",
      "twitter:title": seo.title,
      "twitter:description": seo.description,
      "twitter:image": seo.image,
      "twitter:image:alt": seo.title,
    };
    for (const [name, content] of Object.entries(twitter)) {
      upsertMeta(`meta-${name.replaceAll(":", "-")}`, "name", name, content);
    }

    let structuredData = document.getElementById("structured-data");
    if (!structuredData) {
      structuredData = document.createElement("script");
      structuredData.id = "structured-data";
      structuredData.setAttribute("type", "application/ld+json");
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify(seo.jsonLd).replaceAll(
      "<",
      "\\u003c",
    );
  }, [locale, title]);

  return null;
}
